import { useState, useEffect } from "react";
import api from "../../services/api";
import ImageWithBasePath from "../common/imageWithBasePath";
import { Link } from "react-router-dom";
import { showBasicAlert } from "../common/useAlert";

export type DriveItem = {
  id: number;
  name: string;
  type: number; // 1 for folder, 2 for file
  mime_type: string;
  size: number;
  path: string;
  createdAt: string;
  updatedAt: string;
  document_type: string | null;
};

interface ViewDocumentsModalProps {
  modalId: string;
  signature: string | null;
  companyId: number | null;
  companyType: string | null;
  userId: number | null;
  selectedUserData: { file: DriveItem; access_type: string }[];
}

const ViewDocumentsModal: React.FC<ViewDocumentsModalProps> = ({
  modalId,
  signature,
  companyId,
  companyType,
  userId,
  selectedUserData,
}) => {
  const [quickAccessItems, setQuickAccessItems] = useState<DriveItem[]>([]);
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [sortColumn, setSortColumn] = useState<string>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    const driveItems = selectedUserData
      .filter((item) => item?.file)
      .map((item) => item.file);
    setQuickAccessItems(driveItems);
  }, [selectedUserData]);

  const handleDownloadFile = async (item: DriveItem) => {
    try {
      const data = {
        signature,
        id: item.id,
      };
      const response = await api.post("driveService/downloadDriveItem", data, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", item.name || "unnamed_file");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to download file:", error);
      showBasicAlert("Failed to download file");
    }
  };

  const handleDelete = async (item: DriveItem, targetUserId?: number) => {
    try {
      const userIdToRemove = targetUserId || userId;

      const documentUsers = selectedUserData.filter(
        userDoc => userDoc.file.id === item.id
      );

      const data = {
        signature,
        document_id: item.id,
        ids: [userIdToRemove],
      };

      const deleteResponse = await api.post(
        "driveService/removeUsersFromBulkDocument",
        data
      );

      if (deleteResponse?.data?.message[0]?.status === true) {
        if (!targetUserId || targetUserId === userId) {
          setQuickAccessItems((prevItems) =>
            prevItems.filter((i) => i.id !== item.id)
          );
        }

        showBasicAlert(
          targetUserId
            ? "User access removed successfully"
            : "Your access to document removed successfully"
        );

        if ((!targetUserId || targetUserId === userId) && quickAccessItems.length === 1) {
          const modal = document.getElementById(modalId);
          if (modal) {
            modal.classList.remove('show');
            modal.style.display = 'none';
            document.body.classList.remove('modal-open');
            const modalBackdrop = document.querySelector('.modal-backdrop');
            if (modalBackdrop) {
              modalBackdrop.remove();
            }
          }
        }
      } else {
        showBasicAlert(deleteResponse?.data?.message[0]?.message || "Failed to remove access");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      showBasicAlert("Failed to remove document access");
    }
  };
  const getIconByMime = (item: DriveItem) => {
    if (item.type === 1) return "folder-icon.svg";
    if (!item.name) return "file.svg";

    const extension = item.name?.split(".").pop()?.toLowerCase() || "";

    switch (extension) {
      case "pdf":
        return "pdf-icon.svg";
      case "doc":
      case "docx":
        return "file.svg";
      case "xls":
      case "xlsx":
        return "xls-icon.svg";
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
        return "image.svg";
      default:
        return "file.svg";
    }
  };

  const handleCopy = async (item: DriveItem) => {
    const data = {
      signature,
      file_id: item.id,
    };

    try {
      const response = await api.post("driveService/getFileLink", data);

      if (response.status === 200 && response?.data?.message[0]?.data) {
        const fileLink = response.data.message[0].data.link;
        if (fileLink) {
          await navigator.clipboard.writeText(fileLink);
          showBasicAlert("Link copied");
        } else {
          showBasicAlert("Can't copy this link");
        }
      } else {
        console.error("Error: Link not found in response");
        showBasicAlert("Can't copy this link");
      }
    } catch (error) {
      console.error("Error in handleCopy:", error);
      showBasicAlert("Failed to copy link");
    }
  };

  const handleSort = (column: string) => {
    const isSameColumn = sortColumn === column;
    const newDirection =
      isSameColumn && sortDirection === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortDirection(newDirection);
  };

  const sortedItems = (items: DriveItem[]) =>
    [...items].sort((a, b) => {
      if (a.type === 1 && b.type !== 1) return -1;
      if (a.type !== 1 && b.type === 1) return 1;

      let comparison = 0;
      if (sortColumn === "name") {
        const nameA = a.name || "";
        const nameB = b.name || "";
        comparison = nameA.localeCompare(nameB);
      } else if (sortColumn === "type") {
        const typeA =
          a.type === 1 ? "Folder" : a.document_type || a.mime_type || "";
        const typeB =
          b.type === 1 ? "Folder" : b.document_type || b.mime_type || "";
        comparison = typeA.localeCompare(typeB);
      } else if (sortColumn === "size") {
        comparison = Number(a.size || 0) - Number(b.size || 0);
      } else if (sortColumn === "date") {
        comparison =
          new Date(a.updatedAt || 0).getTime() -
          new Date(b.updatedAt || 0).getTime();
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

  const formatSize = (bytes: number): string => {
    if (!bytes) return "0 Bytes";
    const units = ["Bytes", "KB", "MB", "GB", "TB"];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(2)} ${units[i]}`;
  };

  return (
    <div className="modal fade" id={modalId} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5 className="modal-title">View Documents</h5>
            <div className="d-flex align-items-center">
              <button
                className={`btn btn-sm me-2 ${viewMode === "grid" ? "btn-primary" : "btn-outline-primary"
                  }`}
                onClick={() => setViewMode("grid")}
              >
                <i className="ti ti-layout-grid" /> Grid
              </button>
              <button
                className={`btn btn-sm me-2 ${viewMode === "list" ? "btn-primary" : "btn-outline-primary"
                  }`}
                onClick={() => setViewMode("list")}
              >
                <i className="ti ti-list-details" /> List
              </button>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => setQuickAccessItems([])}
              ></button>
            </div>
          </div>
          <div
            className={
              viewMode === "grid"
                ? "row row-cols-xxl-5 row-cols-xl-3 row-cols-sm-3 row-cols-1 justify-content-center p-3"
                : "file-list-table p-3"
            }
          >
            {quickAccessItems.length === 0 ? (
              <div className="col text-center mt-4">
                <p className="text-muted">No files uploaded.</p>
              </div>
            ) : (
              <>
                {viewMode === "list" && (
                  <div className="row bg-light py-2 border-bottom fw-bold text-muted">
                    <div
                      className="col-md-4 cursor-pointer"
                      onClick={() => handleSort("name")}
                    >
                      Name
                      {sortColumn === "name" && (
                        <i
                          className={`ti ti-chevron-${sortDirection === "asc" ? "up" : "down"
                            } ms-1`}
                        ></i>
                      )}
                    </div>
                    <div
                      className="col-md-2 cursor-pointer"
                      onClick={() => handleSort("type")}
                    >
                      Type
                      {sortColumn === "type" && (
                        <i
                          className={`ti ti-chevron-${sortDirection === "asc" ? "up" : "down"
                            } ms-1`}
                        ></i>
                      )}
                    </div>
                    <div
                      className="col-md-2 cursor-pointer"
                      onClick={() => handleSort("size")}
                    >
                      Size
                      {sortColumn === "size" && (
                        <i
                          className={`ti ti-chevron-${sortDirection === "asc" ? "up" : "down"
                            } ms-1`}
                        ></i>
                      )}
                    </div>
                    <div
                      className="col-md-2 cursor-pointer"
                      onClick={() => handleSort("date")}
                    >
                      Date
                      {sortColumn === "date" && (
                        <i
                          className={`ti ti-chevron-${sortDirection === "asc" ? "up" : "down"
                            } ms-1`}
                        ></i>
                      )}
                    </div>
                    <div className="col-md-2 text-end">Actions</div>
                  </div>
                )}
                {viewMode === "grid"
                  ? quickAccessItems.map((item) => (
                    <div className="col d-flex" key={item.id}>
                      <div className="card access-wrap border-0 flex-fill position-relative">
                        <div className="card-body text-center">
                          <div className="dropdown ms-2 position-absolute top-0 end-0 m-2">
                            <Link
                              to="#"
                              className="d-inline-flex align-items-center"
                              data-bs-toggle="dropdown"
                              id={`dropdown-${item.id}`}
                            >
                              <i className="ti ti-dots" />
                            </Link>
                            <ul className="dropdown-menu dropdown-menu-end p-3">
                              {item.type !== 1 && (
                                <>
                                  <li>
                                    <Link
                                      to="#"
                                      className="dropdown-item rounded-1"
                                      onClick={() => handleDownloadFile(item)}
                                    >
                                      <i className="ti ti-download me-1" />
                                      Download
                                    </Link>
                                  </li>
                                  <li>
                                    <Link
                                      to="#"
                                      className="dropdown-item rounded-1"
                                      onClick={() => handleDelete(item)}
                                    >
                                      <i className="ti ti-trash me-1" />
                                      Delete
                                    </Link>
                                  </li>
                                  <li>
                                    <Link
                                      to="#"
                                      className="dropdown-item rounded-1"
                                      onClick={() => handleCopy(item)}
                                    >
                                      <i className="ti ti-link me-1" />
                                      Copy Link
                                    </Link>
                                  </li>
                                </>
                              )}
                            </ul>
                          </div>
                          <div
                            onDoubleClick={() => item.type === 1}
                            style={{
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            {item.mime_type?.startsWith("image/") ? (
                              <img
                                src={`${process.env.REACT_APP_API_BASE_URL}/Uploads/${item.path}`}
                                alt={item.name || "Image"}
                                style={{
                                  width: "100%",
                                  height: "150px",
                                  objectFit: "cover",
                                  borderRadius: 6,
                                }}
                                className="mb-3"
                              />
                            ) : item.mime_type === "application/pdf" ? (
                              <iframe
                                src={`${process.env.REACT_APP_API_BASE_URL}/Uploads/${item.path}`}
                                title={item.name || "PDF"}
                                style={{
                                  width: "100%",
                                  height: "150px",
                                  border: "none",
                                  borderRadius: 6,
                                }}
                                className="mb-3"
                              />
                            ) : item.mime_type?.startsWith("video/") ? (
                              <video
                                src={`${process.env.REACT_APP_API_BASE_URL}/Uploads/${item.path}`}
                                controls
                                style={{
                                  width: "100%",
                                  height: "150px",
                                  borderRadius: 6,
                                }}
                                className="mb-3"
                              />
                            ) : item.mime_type?.startsWith("audio/") ? (
                              <audio
                                controls
                                style={{
                                  width: "100%",
                                  marginBottom: "12px",
                                }}
                              >
                                <source
                                  src={`${process.env.REACT_APP_API_BASE_URL}/Uploads/${item.path}`}
                                  type={item.mime_type}
                                />
                                Your browser does not support the audio
                                element.
                              </audio>
                            ) : item.mime_type === "application/json" ||
                              item.mime_type === "text/csv" ||
                              item.mime_type?.startsWith("text/") ? (
                              <iframe
                                src={`${process.env.REACT_APP_API_BASE_URL}/Uploads/${item.path}`}
                                title={item.name || "Document"}
                                style={{
                                  width: "100%",
                                  height: "150px",
                                  border: "1px solid #ccc",
                                  borderRadius: 6,
                                }}
                                className="mb-3"
                              />
                            ) : item.mime_type ===
                              "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                              item.mime_type ===
                              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                              item.mime_type ===
                              "application/vnd.openxmlformats-officedocument.presentationml.presentation" ? (
                              <iframe
                                src={`https://docs.google.com/gview?url=${encodeURIComponent(
                                  `${process.env.REACT_APP_API_BASE_URL}/Uploads/${item.path}`
                                )}&embedded=true`}
                                title={item.name || "Document"}
                                style={{
                                  width: "100%",
                                  height: "150px",
                                  border: "1px solid #ccc",
                                  borderRadius: 6,
                                }}
                                className="mb-3"
                              />
                            ) : (
                              <ImageWithBasePath
                                src={`assets/img/icons/${getIconByMime(
                                  item
                                )}`}
                                alt={item.name || "File"}
                                className="mb-3"
                                width={50}
                                height={32}
                              />
                            )}
                          </div>
                          <p
                            className="mb-0 text-truncate"
                            style={{
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "13px",
                            }}
                            title={item.name || ""}
                          >
                            {item.name || "Unnamed File"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                  : sortedItems(quickAccessItems).map((item) => (
                    <div
                      key={item.id}
                      className="row align-items-center py-2 border-bottom hover-shadow"
                    >
                      <div className="col-md-4 d-flex align-items-center gap-2">
                        <ImageWithBasePath
                          src={`assets/img/icons/${getIconByMime(item)}`}
                          alt={item.name || "File"}
                          width={32}
                          height={32}
                        />
                        <strong>{item.name || "Unnamed File"}</strong>
                      </div>
                      <div className="col-md-2">
                        {item.type === 1
                          ? "Folder"
                          : item.document_type || item.mime_type || "File"}
                      </div>
                      <div className="col-md-2">
                        {item.type === 1
                          ? "-"
                          : formatSize(Number(item.size || 0))}
                      </div>
                      <div className="col-md-2">
                        {item.updatedAt
                          ? new Date(item.updatedAt).toLocaleDateString()
                          : "N/A"}
                      </div>
                      <div className="col-md-2 text-end">
                        <div className="dropdown">
                          <Link
                            to="#"
                            className="text-muted"
                            data-bs-toggle="dropdown"
                            id={`dropdown-${item.id}`}
                          >
                            <i className="ti ti-dots" />
                          </Link>
                          <ul className="dropdown-menu dropdown-menu-end p-3">
                            {item.type !== 1 && (
                              <>
                                <li>
                                  <Link
                                    to="#"
                                    className="dropdown-item rounded-1"
                                    onClick={() => handleDownloadFile(item)}
                                  >
                                    <i className="ti ti-download me-1" />
                                    Download
                                  </Link>
                                </li>
                                <li>
                                  <Link
                                    to="#"
                                    className="dropdown-item rounded-1"
                                    onClick={() => handleDelete(item)}
                                  >
                                    <i className="ti ti-trash me-1" />
                                    Delete
                                  </Link>
                                </li>
                                <li>
                                  <Link
                                    to="#"
                                    className="dropdown-item rounded-1"
                                    onClick={() => handleCopy(item)}
                                  >
                                    <i className="ti ti-link me-1" />
                                    Copy Link
                                  </Link>
                                </li>
                              </>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewDocumentsModal;
